import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { getHealth, getPrs, getMyTeam, getInstallationSettingsUrl, getGitHubInstallUrl } from "../api";
import { useTeamSocket } from "../hooks/useTeamSocket";
import { useAuth } from "../auth/AuthContext";
import StatCard from "./StatCard";
import PrCard, { stateConfig } from "./PrCard";
import TeamPanel from "./TeamPanel";
import TeamlessPanel from "./TeamlessPanel";
import AppHeader from "./AppHeader";

const PAGE_SIZE = 50;

// Item 18 redesign: five stacked pill rows read as cluttered even collapsed
// behind a toggle (project owner feedback after the first pass). Replaced
// with native <select> dropdowns — same one-line footprint regardless of
// how many options a dimension has (a 10-person team's reviewer list costs
// nothing extra, unlike pills which wrapped to more rows), free keyboard/
// screen-reader support, and no second custom-built disclosure UI to
// maintain. Only Active/History stays a segmented control below (a mode
// switch, not a narrowing filter, so a dropdown would be the wrong control).
const SELECT_ARROW = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath d='M2 3.5L5 6.5L8 3.5' stroke='%238b95ab' stroke-width='1.3' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 10px center",
};

function FilterSelect({ value, onChange, options, ariaLabel }) {
  const isActive = value !== "all";
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      style={SELECT_ARROW}
      className={`shrink-0 appearance-none rounded-md text-xs font-medium pl-3 pr-7 py-2 cursor-pointer outline-none transition-colors border ${
        isActive
          ? "border-rf-accent-blue/40 bg-rf-accent-blue/10 text-rf-accent-blue"
          : "border-rf-border bg-rf-bg-secondary text-rf-text-secondary hover:border-rf-text-muted"
      }`}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// Item 18 fix: placeholder rows shown only while a background refetch is
// in flight AND there's nothing already on screen to dim — see the PR
// List's render logic below for why this matters (a real "No PRs yet"
// flash bug, not just polish).
function SkeletonRows() {
  return (
    <>
      <div className="h-16 rounded-lg bg-rf-bg-secondary border border-rf-border animate-pulse" />
      <div className="h-16 rounded-lg bg-rf-bg-secondary border border-rf-border animate-pulse" />
    </>
  );
}

function Dashboard({ user, onLogout }) {
  const { justStartedTrial, hasTeam } = useAuth();
  // `=== false` specifically, not `!hasTeam`. hasTeam is null while the team
  // is still being resolved on mount, and treating "unknown" as "teamless"
  // would flash "You're not on a team" at every member on every load — the
  // same false-claim-under-unknown-state trap as gotchas #44/#60.
  const teamless = hasTeam === false;
  const [prs, setPrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Item 18 fix: a filter-triggered refetch is NOT the same event as the
  // very first load — `loading` gates a full-page early return (see below),
  // so reusing it for every filter click was blanking the entire dashboard
  // (header, stats, the filter pills you just clicked) on every interaction.
  // `refreshing` drives a lighter in-place indicator instead.
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [team, setTeam] = useState(null);
  // A team soft-deleted and still inside its 30-day window. Mutually
  // exclusive with `team`, which the view hides while it is pending.
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  // Item 20: dismissed locally only — justStartedTrial itself only ever gets
  // set once per real signup (see AuthContext), so there's nothing to
  // persist here; a refresh naturally never re-shows it.
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false);
  // Item 22: dismissed locally only, per session — unlike the trial banner
  // above, this one is state-driven (user?.contact_email), not a one-time
  // signup flag, so it naturally comes back next session if still unset.
  const [profileBannerDismissed, setProfileBannerDismissed] = useState(false);

  // Item 18: new filter dimensions. view/stateFilter default to "active" /
  // "all" so a fresh load hides merged/closed PRs by default. stats comes
  // straight from the API — it's a stable, always-active-scoped aggregate,
  // never derived from whatever `prs` currently holds (see get-prs's stats
  // query — deliberately unaffected by any of these filters).
  const [view, setView] = useState("active");
  const [stateFilter, setStateFilter] = useState("all");
  const [repoFilter, setRepoFilter] = useState("all");
  const [reviewerFilter, setReviewerFilter] = useState("all");
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ pending: 0, inReview: 0, unassigned: 0, avgTime: 0, avgComplexity: "0.0" });

  // fetchData must never change identity (see below) — it reads current
  // filters from this ref instead of closing over state.
  const filtersRef = useRef({ view, stateFilter, filter, repoFilter, reviewerFilter });
  useEffect(() => {
    filtersRef.current = { view, stateFilter, filter, repoFilter, reviewerFilter };
  }, [view, stateFilter, filter, repoFilter, reviewerFilter]);
  const offsetRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);
  // Item 18 fix: a monotonically increasing request generation. Filter
  // clicks and Load More can both be in flight at once (Load More isn't
  // gated behind the full-page `loading` state, so its pills stay
  // clickable) — without this, a slow Load More response landing after a
  // newer filter change had already replaced `prs` would append stale,
  // wrongly-filtered rows onto the end of the current, correctly-filtered
  // list. Every fetchData call claims the next id; a response is only
  // applied if its id is still the latest by the time it resolves.
  const requestIdRef = useRef(0);

  // useTeamSocket tears down and reopens the WebSocket connection whenever
  // this function's reference changes (see hooks/useTeamSocket.js). Filter
  // state must never enter fetchData's own dependency array, or every
  // filter click would reconnect the socket. Reading filtersRef/offsetRef
  // instead keeps fetchData stable forever while still always fetching with
  // whatever filters are current — including on a WS-triggered refetch.
  const fetchData = useCallback(async ({ append = false } = {}) => {
    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;

    if (append) setLoadingMore(true);
    else if (!hasLoadedOnceRef.current) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      // Kept despite nothing rendering its result: this is the only call in
      // fetchData that can throw into the OUTER catch, so it's what drives the
      // "Failed to connect to API" banner. The PR and team fetches each have
      // their own inner try/catch and swallow their errors. Deleting it along
      // with the DB-table counter would have silently removed the dashboard's
      // only connectivity check.
      await getHealth();

      // Try to fetch real PRs from API
      try {
        const f = filtersRef.current;
        const currentOffset = append ? offsetRef.current : 0;
        const apiParams = { view: f.view, limit: PAGE_SIZE, offset: currentOffset };
        if (f.stateFilter !== "all") apiParams.state = f.stateFilter;
        if (f.filter !== "all") apiParams.priority = f.filter;
        if (f.repoFilter !== "all") apiParams.repository = f.repoFilter;
        if (f.reviewerFilter !== "all") apiParams.reviewer = f.reviewerFilter;

        const prsRes = await getPrs(apiParams);
        // A newer request has since started — drop this response entirely
        // (including the team fetch below) rather than applying half-stale
        // state on top of whatever the newer request already produced.
        if (isStale()) return;
        const data = prsRes.data?.data || {};
        const prData = data.prs || [];
        setTotal(data.total ?? 0);
        if (data.stats) setStats(data.stats);
        offsetRef.current = currentOffset + prData.length;
        setPrs((prev) => (append ? [...prev, ...prData] : prData));
      } catch (prErr) {
        if (isStale()) return;
        // If 401 (no auth) or no data, use empty array
        console.log(
          "Could not fetch PRs from API, showing empty state:",
          prErr.message,
        );
        if (!append) setPrs([]);
      }

      // Try to fetch team data
      try {
        const teamRes = await getMyTeam();
        if (isStale()) return;
        const teamData = teamRes.data?.data?.team;
        if (teamData) {
          setTeam(teamData);
          setTeamMembers(teamData.members || []);
        }
        // Present only when the team is soft-deleted and still inside its
        // window. `team` is null in that case (the view hides it), so without
        // this the user who just deleted their own team would be told "You're
        // not on a team" — false, and it hides the one action that matters.
        setPendingDeletion(teamRes.data?.data?.pending_deletion || null);
      } catch (teamErr) {
        console.log('Could not fetch team:', teamErr.message);
      }
    } catch (err) {
      if (!isStale()) setError("Failed to connect to API: " + err.message);
    } finally {
      if (!isStale()) {
        setLoading(false);
        setLoadingMore(false);
        setRefreshing(false);
        hasLoadedOnceRef.current = true;
      }
    }
  }, []); // stable forever — never add filter state here

  // Refetches from scratch whenever a filter actually changes, without ever
  // changing fetchData's own identity (see above).
  useEffect(() => {
    offsetRef.current = 0;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, stateFilter, filter, repoFilter, reviewerFilter]);

  // Primary refresh path: a WebSocket push tells us the instant a PR
  // opens/closes for this team, so we only refetch when something actually
  // changed rather than on a fixed timer.
  // ⚠️ `hasTeam === true`, NOT `!teamless`. These are different questions and
  // they fail safe in opposite directions:
  //
  //   rendering — "do I claim they have no team?"  -> `=== false`
  //               (never claim it while the answer is unknown)
  //   socket    — "do I open a connection?"        -> `=== true`
  //               (never connect until we know there IS a team)
  //
  // `!teamless` looks equivalent and is not: it is `!(hasTeam === false)`,
  // which is TRUE while hasTeam is still null, so the socket connected on
  // every mount before the team resolved — including for teamless users,
  // which is exactly what this flag exists to prevent. Caught by the test
  // that spies on the WebSocket constructor.
  const wsStatus = useTeamSocket(fetchData, { enabled: hasTeam === true });

  // Fallback safety net for users behind a proxy/firewall that blocks the
  // WebSocket upgrade (or if it ever fails silently) — still a ~36x
  // reduction from the old 30s interval.
  useEffect(() => {
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Every other filter dimension is now applied server-side (see fetchData
  // above) — search is the one dimension that stays client-side, since it's
  // not one of item 18's v1 filter dimensions.
  const visiblePrs = searchTerm
    ? prs.filter((pr) => {
        const term = searchTerm.toLowerCase();
        return (
          (pr.title || "").toLowerCase().includes(term) ||
          (pr.author_github_username || "").toLowerCase().includes(term)
        );
      })
    : prs;

  const hasActiveFilters =
    filter !== "all" || stateFilter !== "all" || repoFilter !== "all" || reviewerFilter !== "all";
  const isMyQueue = !!user?.user_id && reviewerFilter === user.user_id;

  // Every option's label repeats its dimension name (e.g. "Priority: High"),
  // not just the default — a native <select> shows whichever option is
  // selected as its own closed-state text, so that's the only way for the
  // closed dropdown to stay self-explanatory without an external label.
  const priorityOptions = [
    { value: "all", label: "Priority: All" },
    { value: "critical", label: "Priority: Critical" },
    { value: "high", label: "Priority: High" },
    { value: "medium", label: "Priority: Medium" },
    { value: "low", label: "Priority: Low" },
  ];

  const activeStateOptions = [
    { value: "all", label: "State: All" },
    { value: "open", label: `State: ${stateConfig.open.label}` },
    { value: "in_review", label: `State: ${stateConfig.in_review.label}` },
    { value: "awaiting_changes", label: `State: ${stateConfig.awaiting_changes.label}` },
    { value: "approved", label: `State: ${stateConfig.approved.label}` },
  ];
  const historyStateOptions = [
    { value: "all", label: "State: All" },
    { value: "merged", label: `State: ${stateConfig.merged.label}` },
    { value: "closed", label: `State: ${stateConfig.closed.label}` },
  ];
  const stateOptions = view === "history" ? historyStateOptions : activeStateOptions;

  const repoOptions = [
    { value: "all", label: "Repo: All repos" },
    ...(team?.repos || []).map((r) => ({
      value: r.repository_full_name,
      label: `Repo: ${r.repository_full_name.split("/").pop()}`,
    })),
  ];

  // My Queue (below) and this dropdown share the same reviewerFilter state
  // rather than being two competing sources of truth — clicking My Queue
  // just sets reviewerFilter to your own user_id, which this select then
  // reflects as selected.
  const reviewerOptions = [
    { value: "all", label: "Reviewer: All" },
    { value: "unassigned", label: "Reviewer: Unassigned" },
    ...teamMembers.map((m) => ({
      value: m.user_id,
      label: `Reviewer: ${m.display_name || m.github_username}`,
    })),
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-rf-bg-primary flex items-center justify-center">
        <div className="text-rf-text-secondary text-sm">
          Loading dashboard...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-rf-bg-primary">
      <AppHeader user={user} onLogout={onLogout} wsStatus={wsStatus} plan={team?.plan} active="dashboard" />

      {/* Main Content */}
      <main className="max-w-[1200px] mx-auto px-4 sm:px-8 py-7">
        {/* Title */}
        <div className="mb-7 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-rf-text-primary tracking-tight mb-1">
              Review Dashboard
            </h1>
            <p className="text-sm text-rf-text-secondary">
              {team?.team_name || 'No Team'} · {teamMembers.length} active reviewer{teamMembers.length !== 1 ? 's' : ''}
              {team?.repos && ` · ${team.repos.length} connected repo${team.repos.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          {/* Repo selection lives on GitHub now — adding or removing a repo is
              done in the App's installation settings, and the
              installation_repositories webhook syncs it back to us. */}
          <div className="flex flex-wrap gap-2">
            <a
              href={team?.github_installation_id ? getInstallationSettingsUrl(team.github_installation_id) : "#"}
              target="_blank"
              rel="noreferrer"
              className={`px-3 py-2 rounded-md border border-rf-border text-rf-text-secondary text-xs font-medium flex items-center gap-1.5 hover:text-rf-text-primary hover:border-rf-text-secondary transition-colors ${
                team?.github_installation_id ? "" : "opacity-50 pointer-events-none"
              }`}
            >
              <span>⚙</span> Manage repositories on GitHub
            </a>
            <button
              onClick={fetchData}
              className="px-3 py-2 rounded-md border border-rf-border text-rf-text-secondary text-xs font-medium flex items-center gap-1.5 hover:text-rf-text-primary hover:border-rf-text-secondary transition-colors"
            >
              <span>↻</span> Refresh Data
            </button>
          </div>
        </div>

        {/* Trial Start Banner (item 20) — one-time, not auto-dismissing.
            This is the only real confirmation a brand-new team gets that its
            14-day trial started; AppHeader's persistent badge is ambient
            awareness afterward, not a substitute for this moment. */}
        {justStartedTrial && !trialBannerDismissed && (
          <div className="mb-6 p-4 rounded-lg bg-rf-accent/10 border border-rf-accent/30 text-rf-text-primary text-sm flex flex-wrap items-center justify-between gap-3">
            <span>
              <span className="font-semibold text-rf-accent">🎉 Your 14-day Pro trial has started</span>
              {" — "}full access to every feature until{" "}
              <span className="font-semibold">
                {new Date(justStartedTrial.endsAt).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
              . No card required.
            </span>
            <button
              onClick={() => setTrialBannerDismissed(true)}
              aria-label="Dismiss"
              className="shrink-0 text-rf-text-secondary hover:text-rf-text-primary"
            >
              ×
            </button>
          </div>
        )}

        {/* Setup checklist card (item 22) — state-driven on user.contact_email
            being unset, not a one-time signup flag, so it applies equally to
            a brand-new user and an existing one who just hasn't set it yet,
            and disappears permanently and automatically the moment it's set.
            Deliberately not a percentage/progress-bar treatment — there's
            genuinely only one missing field (display_name already has a
            usable GitHub default), and fabricating a second "step" just to
            make a meter look substantive is the kind of artificial-
            incompleteness pattern this product avoids elsewhere. */}
        {!user?.contact_email && !profileBannerDismissed && (
          <div className="mb-6 p-4 rounded-lg bg-rf-accent-blue/10 border border-rf-accent-blue/30 text-rf-text-primary text-sm flex flex-wrap items-center justify-between gap-3">
            <span>
              <span className="font-semibold text-rf-accent-blue">Finish setting up your account</span>
              {" — "}add a contact email so we can reach you about your account and billing.{" "}
              <Link to="/profile" className="underline hover:no-underline">
                Add contact email →
              </Link>
            </span>
            <button
              onClick={() => setProfileBannerDismissed(true)}
              aria-label="Dismiss setup reminder"
              className="shrink-0 text-rf-text-secondary hover:text-rf-text-primary"
            >
              ×
            </button>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-4 rounded-lg bg-rf-accent-danger/10 border border-rf-accent-danger/30 text-rf-accent-danger text-sm">
            {error}
            <button
              onClick={fetchData}
              className="ml-3 underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        {/*
          App-uninstalled banner. Sits above the quota banner deliberately:
          without an installation nothing ingests at all, so it outranks a
          plan-limit warning that only pauses some repos.

          Not dismissible, unlike the trial and profile banners — those are
          informational, this is a product that has stopped working. Webhooks
          land on `No team for installation` and are dropped (gotcha #13), so
          new PRs simply never appear and there is otherwise no explanation on
          screen. The "Manage repositories on GitHub" button above already
          greys itself out in this state; this is what tells you why.

          getGitHubInstallUrl() is the right URL here and one of the few places
          it's safe (gotcha #29): that URL dead-ends for someone who already
          has the App installed, which is precisely the case this branch
          excludes. Do not reuse it anywhere the install state is unknown.
        */}
        {team && !team.github_installation_id && (
          <div className="mb-6 p-4 rounded-lg bg-rf-accent-warning/10 border border-rf-accent-warning/30 text-rf-accent-warning text-sm flex flex-wrap items-center justify-between gap-3">
            <span>
              The Anthurium GitHub App isn't installed, so new pull requests
              won't show up here. Your team and its history are untouched —
              reinstalling reconnects them.
            </span>
            <a
              href={getGitHubInstallUrl()}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 underline hover:no-underline"
            >
              Reinstall on GitHub →
            </a>
          </div>
        )}

        {/* Quota Banner (item 9) */}
        {team?.plan?.quota_flagged && (
          <div className="mb-6 p-4 rounded-lg bg-rf-accent-warning/10 border border-rf-accent-warning/30 text-rf-accent-warning text-sm flex flex-wrap items-center justify-between gap-3">
            <span>Your team has more connected repos or members than your current plan allows.</span>
            <Link to="/team" className="shrink-0 underline hover:no-underline">
              Review now →
            </Link>
          </div>
        )}

        {/*
          Routing excludes the PR author (routing-engine.mjs, `u.user_id != $3`),
          so a team with one eligible reviewer can never route anything its
          only member opens. Those PRs used to sit unassigned with no
          explanation anywhere — this is the explanation, and it lives here
          rather than on PrCard because only the Dashboard knows team
          composition.

          ⚠️ Guarded on `team &&` for a specific reason (gotchas #44/#60, three
          recurrences in four PRs). getMyTeam()'s failure is SWALLOWED above
          (console.log in the inner catch), so on a failed fetch `team` is null
          and `teamMembers` is [] — which makes a bare `teamMembers.length < 2`
          read as TRUE and print "your team is too small" when we simply do not
          know. Unknown state must render nothing, not a claim. `stats` comes
          from the separate PR fetch and can legitimately be populated while
          the team fetch failed, so it cannot rescue this on its own.

          `stats.unassigned` is filter-immune by construction (computed in
          get-prs's own stats query, not derived from the paginated list), so
          this notice does not flicker as the user changes filters.
        */}
        {team && teamMembers.filter((m) => m.plan_active !== false).length < 2 && stats.unassigned > 0 && (
          <div className="mb-6 p-4 rounded-lg bg-rf-accent-blue/10 border border-rf-accent-blue/30 text-rf-accent-blue text-sm flex flex-wrap items-center justify-between gap-3">
            <span>
              {stats.unassigned} pull request{stats.unassigned !== 1 ? 's' : ''} could not be
              assigned a reviewer. Anthurium never routes a PR back to its own author, so
              routing needs at least one other active teammate.
            </span>
            <Link to="/team" className="shrink-0 underline hover:no-underline">
              Invite your team →
            </Link>
          </div>
        )}

        {/*
          Stats Grid.

          Teamless renders an em-dash, not the zeros the API actually sends.
          get-prs genuinely returns { pending: 0, inReview: 0, avgTime: 0,
          avgComplexity: "0.0" } for a user with no team — but "0.0 avg
          complexity" is a claim about measured work, and "0 pending" reads
          as "Anthurium looked and found nothing" rather than "there is
          nothing to look at yet". The em-dash is the honest glyph for
          absent, and TeamPanel already uses it for its unknown state, so
          this is consistent rather than novel.

          The sub-labels go too: "target: 30m" and "/10.0" are advice about
          numbers that do not exist.
        */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            label="Awaiting Changes"
            value={teamless ? "—" : stats.pending}
            sub={teamless ? undefined : "needs author action"}
            accent="#ffa502"
          />
          <StatCard
            label="In Review"
            value={teamless ? "—" : stats.inReview}
            accent="#0099ff"
          />
          <StatCard
            label="Avg Review Time"
            value={teamless ? "—" : `${stats.avgTime}m`}
            sub={teamless ? undefined : "target: 30m"}
            accent="#00d4aa"
          />
          <StatCard
            label="Avg Complexity"
            value={teamless ? "—" : stats.avgComplexity}
            sub={teamless ? undefined : "/10.0"}
            accent="#a855f7"
          />
        </div>

        {/* Main Grid: PR Queue + Team Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-7">
          {/* PR Queue */}
          <div>
            {teamless ? (
              /*
                The teamless panel takes the PR list's slot rather than
                replacing the page, so the layout someone sees now is the
                layout they get once they have a team — the dashboard with
                its content missing, not a different screen.

                It is deliberately the loudest thing in this column: the
                em-dash stats above must read as a CONSEQUENCE of this
                panel, not as a broken dashboard. If it ever becomes
                quieter than the stat row, the hierarchy has failed.

                Everything between the heading and the list is gone —
                Priority Queue's title, the My Queue toggle, the four
                filter dropdowns, the Active/History switch and Load More.
                Every one of them is a control over an empty set, and each
                competes with the only action that matters here.
              */
              <TeamlessPanel
                pendingDeletion={pendingDeletion}
                title="You're not on a team"
                body="Anthurium routes pull requests within a team. Start one to connect your repositories, or join a teammate's with an invite code."
              />
            ) : (
            <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <h2 className="text-base font-semibold text-rf-text-primary">
                Priority Queue
              </h2>
              <div className="flex items-center gap-2">
                {/* Fastest path to the single most common reviewer-filter
                    use ("what's on my plate") — shares reviewerFilter state
                    with the Reviewer dropdown below rather than being a
                    separate, competing selection. */}
                <button
                  onClick={() => setReviewerFilter(isMyQueue ? "all" : user?.user_id)}
                  disabled={!user?.user_id}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors disabled:opacity-40 ${
                    isMyQueue
                      ? "border-rf-accent-blue/40 bg-rf-accent-blue/10 text-rf-accent-blue"
                      : "border-rf-border text-rf-text-secondary hover:text-rf-text-primary hover:border-rf-text-muted"
                  }`}
                >
                  My Queue
                </button>
                {/* Active/History is a mode switch, not a narrowing filter —
                    a segmented control, not a dropdown or a pill row. */}
                <div className="inline-flex bg-rf-bg-tertiary rounded-lg p-0.5 gap-0.5">
                  {[{ key: "active", label: "Active" }, { key: "history", label: "History" }].map((v) => (
                    <button
                      key={v.key}
                      onClick={() => { setView(v.key); setStateFilter("all"); }}
                      className={`px-3.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        view === v.key
                          ? "bg-rf-bg-secondary text-rf-accent-blue shadow-sm"
                          : "text-rf-text-secondary hover:text-rf-text-primary"
                      }`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Item 18 redesign: one row of dropdowns instead of five pill
                rows. Fixed single row (flex-nowrap + horizontal scroll)
                rather than wrapping to 2+ lines on narrow viewports — a wrap
                made the row's height vary with viewport width and pushed the
                PR list down unpredictably; a fixed-height scrollable row
                (the same pattern this app already uses elsewhere for
                horizontally-scrolling content, e.g. the analytics data
                tables) keeps layout stable and is a familiar mobile pattern
                (swipeable filter/chip bars). */}
            <div className="flex flex-nowrap items-center gap-2 mb-4 overflow-x-auto">
              <FilterSelect ariaLabel="Priority filter" options={priorityOptions} value={filter} onChange={setFilter} />
              <FilterSelect ariaLabel="State filter" options={stateOptions} value={stateFilter} onChange={setStateFilter} />
              <FilterSelect ariaLabel="Repository filter" options={repoOptions} value={repoFilter} onChange={setRepoFilter} />
              <FilterSelect ariaLabel="Reviewer filter" options={reviewerOptions} value={reviewerFilter} onChange={setReviewerFilter} />
              {hasActiveFilters && (
                <button
                  onClick={() => { setFilter("all"); setStateFilter("all"); setRepoFilter("all"); setReviewerFilter("all"); }}
                  className="shrink-0 text-xs text-rf-accent-danger hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>

            {/* Search */}
            <input
              type="text"
              placeholder="Search PRs by title or author..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border border-rf-border bg-rf-bg-secondary text-rf-text-primary text-sm font-sans outline-none focus:border-rf-accent-blue/50 transition-colors mb-4"
            />

            {/* PR List — shows skeleton placeholders during any filter-
                triggered refetch, rather than the whole page unmounting to
                a loading screen (that only happens for the true first
                load, via `loading`). Originally this only swapped to
                skeletons when `prs` was already empty (dimming the stale
                list otherwise) — but that made the loading state
                practically invisible in the common case of filtering a
                non-empty list, since a 150ms opacity dim barely reads as
                "loading" against a typical round-trip. Always showing
                skeletons while refreshing is simpler and actually visible. */}
            <div className="flex flex-col gap-3">
              {refreshing ? (
                <SkeletonRows />
              ) : visiblePrs.length > 0 ? (
                visiblePrs.map((pr) => <PrCard key={pr.pr_id} pr={pr} />)
              ) : (
                <div className="text-center py-12 text-rf-text-muted">
                  <div className="text-3xl mb-2">🎉</div>
                  <div className="text-sm">
                    {searchTerm
                      ? "No PRs match your search"
                      : hasActiveFilters
                        ? "No PRs match your filters"
                        : view === "history"
                          ? "No merged or closed PRs yet"
                          : team?.repos?.length
                            ? "No PRs yet. Open a PR on a connected repo to see it here!"
                            : "No repositories connected yet — add one from Manage repositories on GitHub."}
                  </div>
                </div>
              )}
            </div>

            {/* Load More — History view only for v1; Active stays single-page */}
            {view === "history" && prs.length < total && (
              <button
                onClick={() => fetchData({ append: true })}
                disabled={loadingMore}
                className="mt-3 w-full px-3 py-2 rounded-md border border-rf-border text-rf-text-secondary text-xs font-medium hover:text-rf-text-primary hover:border-rf-text-secondary transition-colors disabled:opacity-50"
              >
                {loadingMore ? "Loading…" : `Load more (${prs.length} of ${total})`}
              </button>
            )}
            </>
            )}
          </div>

          {/* Right Sidebar */}
          <div>
            {/*
              `loaded` is not decoration. The team fetch's error is swallowed
              above, so teamMembers is [] on failure just as it is for a
              genuinely empty team — without this the panel would print "No
              team members found" about a team it never managed to load
              (gotchas #44/#60). Passing `team` truthiness lets it say
              "unavailable" instead of inventing a fact.
            */}
            <TeamPanel members={teamMembers} loaded={!!team} teamless={teamless} />
          </div>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
